import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDriveScannerStatus,
  getScanEmailStatus,
} from 'store/organisation/actions';
import { getScanEmailDetails } from 'store/organisation/selectors';
import { getTaxRatesGettingTaxes } from 'store/tax-rates/selectors';
import { getReturnLoading } from 'store/returns/selectors';

const CAPTURE_TAB_MIN_SKELETON_MS = 280;

/** Orgs whose capture shell has already been shown this session — never re-skeleton. */
const shellReadyOrgIds = new Set<string>();

/** True once the Capture Documents tab shell has loaded for this entity (this browser session). */
export function isCaptureTabShellReady(organisationId?: string): boolean {
  return Boolean(organisationId && shellReadyOrgIds.has(organisationId));
}

export type UseCaptureTabInitialShellOptions = {
  /** Only evaluate while the Capture Documents tab is active. */
  enabled: boolean;
  loading: boolean;
  organisationId?: string;
  /** When true, wait for scan-email / drive-scanner status before first reveal. */
  needsSidePanelData: boolean;
  /** When true, wait for tax-rate / return-type fetches used by the upload checklist. */
  needsTaxSetupData: boolean;
};

/**
 * True only during the first Capture-tab load for an entity (per browser session).
 * Stays false after the shell has been revealed once, including during uploads,
 * tab switches, and background tax-rate refetches.
 */
export function useCaptureTabInitialShell({
  enabled,
  loading,
  organisationId,
  needsSidePanelData,
  needsTaxSetupData,
}: UseCaptureTabInitialShellOptions): boolean {
  const dispatch = useDispatch();
  const scanEmailDetails = useSelector(getScanEmailDetails);
  const gettingTaxes = useSelector(getTaxRatesGettingTaxes);
  const returnLoading = useSelector(getReturnLoading);

  const alreadyShellReady = Boolean(
    organisationId && shellReadyOrgIds.has(organisationId)
  );

  const [sidePanelReady, setSidePanelReady] = React.useState(
    !needsSidePanelData || alreadyShellReady
  );
  const [minDelayElapsed, setMinDelayElapsed] = React.useState(
    alreadyShellReady
  );
  const sidePanelSnapshotRef = React.useRef<typeof scanEmailDetails>(undefined);
  const awaitingSidePanelRef = React.useRef(false);
  const scanEmailDetailsRef = React.useRef(scanEmailDetails);
  scanEmailDetailsRef.current = scanEmailDetails;

  React.useEffect(() => {
    if (!enabled || !organisationId || alreadyShellReady) {
      setMinDelayElapsed(true);
      return;
    }
    setMinDelayElapsed(false);
    const timer = window.setTimeout(
      () => setMinDelayElapsed(true),
      CAPTURE_TAB_MIN_SKELETON_MS
    );
    return () => window.clearTimeout(timer);
  }, [enabled, organisationId, alreadyShellReady]);

  React.useEffect(() => {
    if (!enabled || loading || !organisationId || alreadyShellReady) {
      if (!needsSidePanelData || alreadyShellReady) {
        setSidePanelReady(true);
      }
      return;
    }

    if (!needsSidePanelData) {
      setSidePanelReady(true);
      return;
    }

    setSidePanelReady(false);
    awaitingSidePanelRef.current = true;
    sidePanelSnapshotRef.current = scanEmailDetailsRef.current;
    dispatch(getDriveScannerStatus(organisationId));
    dispatch(getScanEmailStatus(organisationId));
  }, [
    alreadyShellReady,
    dispatch,
    enabled,
    loading,
    needsSidePanelData,
    organisationId,
  ]);

  React.useEffect(() => {
    if (!awaitingSidePanelRef.current || !needsSidePanelData) {
      return;
    }
    if (scanEmailDetails !== sidePanelSnapshotRef.current) {
      setSidePanelReady(true);
      awaitingSidePanelRef.current = false;
    }
  }, [needsSidePanelData, scanEmailDetails]);

  const taxDataReady =
    alreadyShellReady ||
    !needsTaxSetupData ||
    (!gettingTaxes && !returnLoading);

  const sidePanelDataReady =
    alreadyShellReady || !needsSidePanelData || sidePanelReady;

  const initialDataReady =
    alreadyShellReady ||
    (!loading &&
      Boolean(organisationId) &&
      minDelayElapsed &&
      taxDataReady &&
      sidePanelDataReady);

  React.useEffect(() => {
    if (enabled && initialDataReady && organisationId) {
      shellReadyOrgIds.add(organisationId);
    }
  }, [enabled, initialDataReady, organisationId]);

  return (
    enabled &&
    Boolean(organisationId) &&
    !alreadyShellReady &&
    !initialDataReady
  );
}
